// Report data processor for chatbot responses

// Key data extracted from the report
export const reportData = {
  totalReceived: "2,806,744",
  totalDistributed: "2,366,729", 
  unaccounted: "407,683",
  
  topDonors: [
    { country: "UAE", amount: "987,822", rank: 1 },
    { country: "Saudi Arabia", amount: "891,133", rank: 2 },
    { country: "Turkey", amount: "319,470", rank: 3 },
    { country: "Direct Purchases", amount: "256,815", rank: 4 },
    { country: "Pakistan", amount: "134,304", rank: 5 }
  ],
  
  aidCategories: [
    { category: "Medicine", received: "803,180", distributed: "709,338" },
    { category: "Food", received: "726,306", distributed: "335,922" },
    { category: "Shelter", received: "564,809", distributed: "564,455" },
    { category: "Hygiene", received: "304,766", distributed: "391,769" },
    { category: "Other", received: "159,286", distributed: "159,286" }
  ],
  
  agencies: [
    { name: "Higher Relief Committee", received: "2,550,000", percentage: "90%" },
    { name: "Government Emergency Committee", received: "231,694", percentage: "8%" },
    { name: "South Council", received: "22,600", percentage: "1%" }
  ],
  
  monthlyData: [
    { month: "September 2024", received: "17,012", distributed: "12,461" },
    { month: "October 2024", received: "1,931,490", distributed: "753,430" },
    { month: "November 2024", received: "418,639", distributed: "1,164,000" },
    { month: "December 2024", received: "207,909", distributed: "206,556" },
    { month: "Undetermined", received: "231,694", distributed: "229,970" }
  ],
  
  issues: [
    {
      title: "Unit Measurement Inconsistencies",
      description: "Different agencies use different measurement units for the same items",
      example: "Emergency Committee records dates as 'boxes' while Higher Relief Authority records them as 'cans'"
    },
    {
      title: "Receipt vs Distribution Mismatches", 
      description: "Distribution amounts don't match receipt amounts",
      example: "Kuwait aid: 42,300 units received but 93,206 units distributed"
    },
    {
      title: "Missing Geographic Data",
      description: "71% of distributions marked as 'unknown location'",
      impact: "Makes tracking aid delivery impossible"
    },
    {
      title: "Inter-agency Coordination Failures",
      description: "Multiple agencies recording same aid differently",
      example: "Turkish biscuits: HRC transferred 5,940 pieces, GEC received 720 boxes"
    }
  ],
  
  transparencyIssues: [
    {
      issue: "Non-responsive Agencies",
      description: "Some ministries did not respond to information requests"
    },
    {
      issue: "Incomplete Data Sharing", 
      description: "Agencies provided general website references instead of detailed datasets"
    },
    {
      issue: "PDF-only Reports",
      description: "Data published in PDF format limits analysis capabilities"
    },
    {
      issue: "Anti-Corruption Commission Refusal",
      description: "National Anti-Corruption Commission refused to disclose information citing ongoing investigations"
    }
  ],
  
  discrepancyCases: [
    {
      country: "Kuwait",
      issue: "42.3 tons received became 93,206 units distributed",
      severity: "Critical"
    },
    {
      country: "Turkey", 
      issue: "Biscuits: 5,940 pieces transferred but 720 boxes received (8.25 pieces per box?)",
      severity: "High"
    },
    {
      country: "UAE",
      issue: "6 tons of sugar missing, 1 ton of lentils appeared from nowhere",
      severity: "High"
    },
    {
      country: "Egypt",
      issue: "~2,000 blankets and 21 tons of food materials unaccounted",
      severity: "Medium"
    }
  ]
};

// Enhanced search function with more flexible matching
export const searchReportData = (query) => {
  const lowerQuery = query.toLowerCase();
  const results = [];
  
  // Enhanced keyword matching with more variations
  const keywords = {
    totals: [
      'total', 'amount', 'sum', 'how much', 'how many', 'received', 'distributed', 'units',
      'مجموع', 'إجمالي', 'كمية', 'عدد', 'كم', 'مستلم', 'موزع', 'وحدة', 'وحدات'
    ],
    donors: [
      'donor', 'country', 'countries', 'who gave', 'who donated', 'which country', 'top donor', 'biggest donor',
      'uae', 'saudi', 'turkey', 'pakistan', 'kuwait', 'egypt', 'emirates',
      'مانح', 'مانحين', 'دول', 'دولة', 'من أعطى', 'من تبرع', 'أي دولة', 'أكبر مانح',
      'الإمارات', 'السعودية', 'تركيا', 'باكستان', 'الكويت', 'مصر'
    ],
    categories: [
      'categor', 'type', 'kind', 'what type', 'medicine', 'food', 'shelter', 'hygiene', 'medical',
      'فئة', 'فئات', 'نوع', 'أنواع', 'أي نوع', 'دواء', 'طعام', 'مأوى', 'نظافة', 'طبي', 'طبية'
    ],
    issues: [
      'problem', 'issue', 'wrong', 'error', 'mistake', 'discrepancy', 'missing', 'lost', 'disappeared',
      'مشكل', 'مشاكل', 'خلل', 'خطأ', 'غلط', 'تناقض', 'مفقود', 'ضائع', 'اختفى'
    ],
    transparency: [
      'transparen', 'accountab', 'corrupt', 'hide', 'secret', 'refuse', 'deny', 'access',
      'شفافية', 'مساءلة', 'فساد', 'إخفاء', 'سري', 'رفض', 'منع', 'وصول'
    ],
    monthly: [
      'month', 'time', 'when', 'date', 'period', 'september', 'october', 'november', 'december',
      'شهر', 'أشهر', 'وقت', 'متى', 'تاريخ', 'فترة', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
      'أيلول', 'تشرين', 'كانون'
    ],
    agencies: [
      'agenc', 'entit', 'organization', 'committee', 'council', 'ministry', 'government', 'who received',
      'hrc', 'gec', 'higher relief', 'emergency committee', 'south council',
      'جهة', 'جهات', 'هيئة', 'منظمة', 'لجنة', 'مجلس', 'وزارة', 'حكومة', 'من استلم',
      'الهيئة العليا', 'لجنة الطوارئ', 'مجلس الجنوب'
    ]
  };
  
  // Check each category for matches
  Object.entries(keywords).forEach(([category, words]) => {
    const hasMatch = words.some(word => lowerQuery.includes(word.toLowerCase()));
    if (hasMatch) {
      switch (category) {
        case 'totals':
          results.push({
            type: 'totals',
            data: {
              received: reportData.totalReceived,
              distributed: reportData.totalDistributed,
              unaccounted: reportData.unaccounted
            }
          });
          break;
        case 'donors':
          results.push({
            type: 'donors',
            data: reportData.topDonors
          });
          break;
        case 'categories':
          results.push({
            type: 'categories',
            data: reportData.aidCategories
          });
          break;
        case 'issues':
          results.push({
            type: 'issues',
            data: reportData.issues
          });
          break;
        case 'transparency':
          results.push({
            type: 'transparency',
            data: reportData.transparencyIssues
          });
          break;
        case 'monthly':
          results.push({
            type: 'monthly',
            data: reportData.monthlyData
          });
          break;
        case 'agencies':
          results.push({
            type: 'agencies',
            data: reportData.agencies
          });
          break;
      }
    }
  });
  
  // If no specific matches, provide general overview for common question patterns
  if (results.length === 0) {
    const generalQuestions = [
      'what', 'how', 'why', 'where', 'when', 'who', 'tell me', 'explain', 'show me', 'overview', 'summary',
      'ما', 'كيف', 'لماذا', 'أين', 'متى', 'من', 'أخبرني', 'اشرح', 'أرني', 'نظرة عامة', 'ملخص'
    ];
    
    const isGeneralQuestion = generalQuestions.some(word => lowerQuery.includes(word));
    if (isGeneralQuestion || lowerQuery.length < 3) { // Also handle very short queries
      // Provide a comprehensive overview
      results.push(
        { type: 'totals', data: { received: reportData.totalReceived, distributed: reportData.totalDistributed, unaccounted: reportData.unaccounted }},
        { type: 'donors', data: reportData.topDonors.slice(0, 3) }, // Top 3 donors
        { type: 'issues', data: reportData.issues.slice(0, 2) } // Top 2 issues
      );
    }
  }
  
  // Handle specific country questions
  const countryNames = ['uae', 'emirates', 'saudi', 'turkey', 'pakistan', 'kuwait', 'egypt', 'الإمارات', 'السعودية', 'تركيا', 'باكستان', 'الكويت', 'مصر'];
  const mentionsCountry = countryNames.some(country => lowerQuery.includes(country));
  if (mentionsCountry && !results.some(r => r.type === 'donors')) {
    results.push({ type: 'donors', data: reportData.topDonors });
  }
  
  // Handle specific aid type questions
  const aidTypes = ['medicine', 'medical', 'food', 'shelter', 'hygiene', 'دواء', 'طبي', 'طعام', 'مأوى', 'نظافة'];
  const mentionsAidType = aidTypes.some(type => lowerQuery.includes(type));
  if (mentionsAidType && !results.some(r => r.type === 'categories')) {
    results.push({ type: 'categories', data: reportData.aidCategories });
  }
  
  return results;
};

// Generate contextual responses based on search results
export const generateResponse = (query, language = 'en') => {
  const lowerQuery = query.toLowerCase();
  const results = searchReportData(query);
  
  // Handle specific questions that might not match keywords but are common
  if (results.length === 0) {
    // Check for greeting or general help
    if (lowerQuery.includes('hello') || lowerQuery.includes('hi') || lowerQuery.includes('مرحبا') || lowerQuery.includes('أهلا')) {
      return language === 'ar'
        ? 'مرحباً! أنا مساعد تقرير المساعدات الإنسانية. يمكنني مساعدتك في فهم توزيع المساعدات في لبنان خلال العدوان الإسرائيلي 2024. اسألني عن أي شيء!'
        : 'Hello! I\'m the humanitarian aid report assistant. I can help you understand aid distribution in Lebanon during the 2024 Israeli aggression. Ask me anything!';
    }
    
    // Check for help requests
    if (lowerQuery.includes('help') || lowerQuery.includes('مساعدة')) {
      return language === 'ar'
        ? 'يمكنني الإجابة على أسئلة حول:\n• إجمالي المساعدات المستلمة والموزعة\n• الدول المانحة الرئيسية\n• أنواع المساعدات (طبية، غذائية، مأوى)\n• المشاكل في التوزيع\n• قضايا الشفافية\n• البيانات الشهرية\n• الجهات الحكومية المعنية'
        : 'I can answer questions about:\n• Total aid received and distributed\n• Top donor countries\n• Types of aid (medical, food, shelter)\n• Distribution problems\n• Transparency issues\n• Monthly data\n• Government agencies involved';
    }
    
    // Provide a helpful default with key statistics
    return language === 'ar' 
      ? `لم أجد معلومات محددة حول "${query}"، لكن إليك نظرة عامة:\n\n📊 إجمالي المساعدات: ${reportData.totalReceived} وحدة مستلمة، ${reportData.totalDistributed} وحدة موزعة\n🏆 أكبر المانحين: الإمارات، السعودية، تركيا\n⚠️ مشاكل رئيسية: تناقضات في وحدات القياس، مساعدات مفقودة\n\nجرب أسئلة مثل: "ما هي المشاكل؟" أو "من هم أكبر المانحين؟"`
      : `I couldn't find specific information about "${query}", but here's an overview:\n\n📊 Total aid: ${reportData.totalReceived} units received, ${reportData.totalDistributed} units distributed\n🏆 Top donors: UAE, Saudi Arabia, Turkey\n⚠️ Key issues: Unit measurement inconsistencies, missing aid\n\nTry questions like: "What are the problems?" or "Who are the top donors?"`;
  }
  
  let response = '';
  
  results.forEach(result => {
    switch (result.type) {
      case 'totals':
        response += language === 'ar'
          ? `📊 **إحصائيات المساعدات الإجمالية:**\n• المساعدات المستلمة: ${result.data.received} وحدة\n• المساعدات الموزعة: ${result.data.distributed} وحدة\n• الفرق غير المحسوب: ${result.data.unaccounted} وحدة\n\n`
          : `📊 **Total Aid Statistics:**\n• Aid received: ${result.data.received} units\n• Aid distributed: ${result.data.distributed} units\n• Unaccounted difference: ${result.data.unaccounted} units\n\n`;
        break;
        
      case 'donors':
        response += language === 'ar'
          ? '🏆 **أكبر الدول المانحة:**\n' + result.data.map(d => `${d.rank}. ${d.country}: ${d.amount} وحدة`).join('\n') + '\n\n'
          : '🏆 **Top Donor Countries:**\n' + result.data.map(d => `${d.rank}. ${d.country}: ${d.amount} units`).join('\n') + '\n\n';
        break;
        
      case 'categories':
        response += language === 'ar'
          ? '📦 **فئات المساعدات:**\n' + result.data.map(c => `• ${c.category}: ${c.received} مستلم، ${c.distributed} موزع`).join('\n') + '\n\n'
          : '📦 **Aid Categories:**\n' + result.data.map(c => `• ${c.category}: ${c.received} received, ${c.distributed} distributed`).join('\n') + '\n\n';
        break;
        
      case 'issues':
        response += language === 'ar'
          ? '⚠️ **المشاكل الرئيسية المحددة:**\n' + result.data.map(i => `• **${i.title}**: ${i.description}`).join('\n') + '\n\n'
          : '⚠️ **Main Issues Identified:**\n' + result.data.map(i => `• **${i.title}**: ${i.description}`).join('\n') + '\n\n';
        break;
        
      case 'transparency':
        response += language === 'ar'
          ? '🔍 **قضايا الشفافية:**\n' + result.data.map(t => `• **${t.issue}**: ${t.description}`).join('\n') + '\n\n'
          : '🔍 **Transparency Issues:**\n' + result.data.map(t => `• **${t.issue}**: ${t.description}`).join('\n') + '\n\n';
        break;
        
      case 'monthly':
        response += language === 'ar'
          ? '📅 **البيانات الشهرية:**\n' + result.data.map(m => `• ${m.month}: ${m.received} مستلم، ${m.distributed} موزع`).join('\n') + '\n\n'
          : '📅 **Monthly Data:**\n' + result.data.map(m => `• ${m.month}: ${m.received} received, ${m.distributed} distributed`).join('\n') + '\n\n';
        break;
        
      case 'agencies':
        response += language === 'ar'
          ? '🏛️ **الجهات الحكومية الرئيسية:**\n' + result.data.map(a => `• ${a.name}: ${a.received} وحدة (${a.percentage})`).join('\n') + '\n\n'
          : '🏛️ **Main Government Agencies:**\n' + result.data.map(a => `• ${a.name}: ${a.received} units (${a.percentage})`).join('\n') + '\n\n';
        break;
    }
  });
  
  return response.trim();
};

export default { reportData, searchReportData, generateResponse };
